import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useGlobalSettingState } from './GlobalSettingUtils';
import { useWebSocket } from '../WebSocketContext';
import PluginEditorDialog from './PluginEditorDialog';
import PluginVibeConfirmationDialog from './PluginVibeConfirmationDialog';
import PluginImportButton from '../PluginImportButton';
import DeletedPluginsPanel from './DeletedPluginsPanel';
import { apiBinaryRequest, apiRequest } from '../../Utils/apiClient';
import {
  deletePlugin,
  fetchPluginLogs,
  fetchPluginVersions,
  reloadPlugins,
  restorePluginVersion,
  savePluginFiles
} from '../../Utils/adminSettingsApi';

const PluginSettings = forwardRef((_props, ref) => {
  const { t, openSnackbar } = useGlobalSettingState();
  const { sendMessage, subscribeToMessages } = useWebSocket();
  useImperativeHandle(ref, () => ({}));

  const [plugins, setPlugins] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletedPluginsRefresh, setDeletedPluginsRefresh] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [vibeLoading, setVibeLoading] = useState(false);
  const [vibeRequest, setVibeRequest] = useState('');
  const [vibePreviewReady, setVibePreviewReady] = useState(false);
  const [vibeConfirmOpen, setVibeConfirmOpen] = useState(false);
  const [vibeAnalysis, setVibeAnalysis] = useState(null);
  const [vibeClarificationAnswers, setVibeClarificationAnswers] = useState({});

  const [selectedFolder, setSelectedFolder] = useState('');
  const [pluginName, setPluginName] = useState('');
  const [pluginType, setPluginType] = useState('');
  const [pluginKind, setPluginKind] = useState('');
  const [manifestFile, setManifestFile] = useState('plugin.json');
  const [scriptFile, setScriptFile] = useState('plugin.py');

  const [pluginJson, setPluginJson] = useState('');
  const [pluginPy, setPluginPy] = useState('');
  const [memoryJson, setMemoryJson] = useState('');
  const [pluginLogs, setPluginLogs] = useState([]);
  const [pluginLogsLoading, setPluginLogsLoading] = useState(false);
  const [pluginLogsError, setPluginLogsError] = useState('');
  const [pluginVersions, setPluginVersions] = useState([]);
  const [pluginVersionsLoading, setPluginVersionsLoading] = useState(false);
  const [pluginVersionsError, setPluginVersionsError] = useState('');
  const [restoringVersionId, setRestoringVersionId] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [memoryDialogOpen, setMemoryDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const applyPluginFilesPayload = useCallback((data) => {
    const plugin = data.plugin || {};
    setSelectedFolder(plugin.folderName || '');
    setPluginName(plugin.name || plugin.folderName || '');
    setPluginType(plugin.type || '');
    setPluginKind(plugin.kind || '');
    setManifestFile(plugin.manifestFile || 'plugin.json');
    setScriptFile(plugin.scriptFile || 'plugin.py');
    setPluginJson(data.pluginJson || '');
    setPluginPy(data.pluginPy || '');
    setMemoryJson(data.memoryJson || '');
    setHasChanges(false);
    setVibePreviewReady(false);
    setVibeConfirmOpen(false);
    setVibeAnalysis(null);
    setVibeClarificationAnswers({});
  }, []);

  const loadPluginLogs = useCallback(async (folderName) => {
    const normalizedFolder = String(folderName || '').trim();
    if (!normalizedFolder) {
      setPluginLogs([]);
      setPluginLogsError('');
      return;
    }

    setPluginLogsLoading(true);
    setPluginLogs([]);
    setPluginLogsError('');
    try {
      const data = await fetchPluginLogs(normalizedFolder, 100);
      setPluginLogs(Array.isArray(data?.logs) ? data.logs : []);
    } catch (error) {
      setPluginLogs([]);
      setPluginLogsError(
        error?.message || t('global_setting.plugins_logs_error', 'Error loading plugin logs')
      );
    } finally {
      setPluginLogsLoading(false);
    }
  }, [t]);

  const loadPluginVersions = useCallback(async (folderName) => {
    const normalizedFolder = String(folderName || '').trim();
    if (!normalizedFolder) {
      setPluginVersions([]);
      setPluginVersionsError('');
      return;
    }

    setPluginVersionsLoading(true);
    setPluginVersionsError('');
    try {
      const data = await fetchPluginVersions(normalizedFolder, 100);
      setPluginVersions(Array.isArray(data?.versions) ? data.versions : []);
    } catch (error) {
      setPluginVersions([]);
      setPluginVersionsError(
        error?.message || t('global_setting.plugins_versions_error', 'Error loading plugin versions')
      );
    } finally {
      setPluginVersionsLoading(false);
    }
  }, [t]);

  const loadPluginFiles = useCallback(async (folderName) => {
    const normalizedFolder = String(folderName || '').trim();
    if (!normalizedFolder) {
      return;
    }

    setLoadingFiles(true);
    try {
      const data = await apiRequest(`/api/v1/settings/plugins/${encodeURIComponent(normalizedFolder)}/files`);
      applyPluginFilesPayload(data);
      await loadPluginVersions(normalizedFolder);
    } catch (error) {
      openSnackbar(
        error?.message || t('global_setting.plugins_files_load_error', 'Error loading plugin files'),
        3500
      );
    } finally {
      setLoadingFiles(false);
    }
  }, [apiRequest, applyPluginFilesPayload, loadPluginVersions, openSnackbar, t]);

  const loadPlugins = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await apiRequest('/api/v1/settings/plugins');
      const incomingPlugins = Array.isArray(data.plugins) ? data.plugins : [];
      setPlugins(incomingPlugins);

      if (!incomingPlugins.length) {
        setSelectedFolder('');
        setPluginName('');
        setPluginType('');
        setPluginKind('');
        setPluginJson('');
        setPluginPy('');
        setMemoryJson('');
        setPluginLogs([]);
        setPluginLogsError('');
        setPluginVersions([]);
        setPluginVersionsError('');
        setHasChanges(false);
        setVibeConfirmOpen(false);
        setVibeAnalysis(null);
        return;
      }

      const hasSelected = incomingPlugins.some((plugin) => plugin.folderName === selectedFolder);
      const nextFolder = hasSelected ? selectedFolder : incomingPlugins[0].folderName;
      if (nextFolder) {
        if (nextFolder !== selectedFolder) {
          setSelectedFolder(nextFolder);
        }
        await loadPluginFiles(nextFolder);
      }
    } catch (error) {
      openSnackbar(
        error?.message || t('global_setting.plugins_load_error', 'Error loading plugins'),
        3500
      );
    } finally {
      setLoadingList(false);
    }
  }, [apiRequest, loadPluginFiles, openSnackbar, selectedFolder, t]);

  const getPluginKindMeta = useCallback((plugin) => {
    const normalizedKind = String(plugin?.kind || '').trim().toLowerCase();
    if (normalizedKind === 'connector') {
      return {
        label: t('global_setting.plugins_kind_connector', 'Data Connector'),
        color: 'primary'
      };
    }
    if (normalizedKind) {
      return {
        label: t('global_setting.plugins_kind_action', 'Action'),
        color: 'success'
      };
    }
    return {
      label: t('global_setting.plugins_kind_unknown', 'Unknown'),
      color: 'default'
    };
  }, [t]);

  const isAiGeneratedPlugin = useCallback((plugin) => {
    const typeValue = String(plugin?.type || '').trim();
    const nameValue = String(plugin?.name || '').trim();
    const descriptionValue = String(plugin?.description || '').trim();
    return plugin?.isAIGenerated === true
      || plugin?.hasMemory === true
      || /^auto_/i.test(typeValue)
      || /^auto_/i.test(nameValue)
      || /_auto_/i.test(typeValue)
      || /generated by ai/i.test(descriptionValue);
  }, []);

  useEffect(() => {
    loadPlugins();
  }, [loadPlugins]);

  useEffect(() => {
    if (typeof subscribeToMessages !== 'function') {
      return undefined;
    }
    const unsubscribe = subscribeToMessages(
      ['vibeEditGeneratedPlugin', 'vibeApplyGeneratedPluginEdit'],
      (message) => {
        const payload = message?.data || {};
        const isAnalysisResponse = message?.command === 'vibeEditGeneratedPlugin';
        if (
          payload.folderName
          && selectedFolder
          && String(payload.folderName) !== String(selectedFolder)
        ) {
          return;
        }
        if (payload.status === 'processing') {
          setVibeLoading(true);
          return;
        }
        setVibeLoading(false);
        if (payload.status === 'error') {
          if (!isAnalysisResponse) {
            setVibePreviewReady(false);
          }
          openSnackbar(
            payload.message || t('global_setting.plugins_vibe_error', 'Error generating AI edit proposal'),
            4000
          );
          return;
        }
        if (payload.status !== 'success') {
          return;
        }
        if (isAnalysisResponse) {
          if (!payload.analysis || !String(payload.analysis.understanding || '').trim()) {
            openSnackbar(
              t('global_setting.plugins_vibe_error', 'Error generating AI edit proposal'),
              4000
            );
            return;
          }
          setVibeAnalysis(payload.analysis);
          setVibeClarificationAnswers({});
          setVibeConfirmOpen(true);
          const hasClarifyingQuestions = Array.isArray(payload.analysis.clarifying_questions)
            && payload.analysis.clarifying_questions.length > 0;
          openSnackbar(
            payload.analysis.can_proceed === false || hasClarifyingQuestions
              ? t('global_setting.plugins_vibe_needs_clarification', 'The AI found questions or risks. Review them before continuing.')
              : t('global_setting.plugins_vibe_understanding_ready', 'AI understanding ready. Review it before generating code.'),
            3500
          );
          return;
        }
        if (!payload.pluginJson || !payload.pluginPy) {
          setVibePreviewReady(false);
          openSnackbar(
            t('global_setting.plugins_vibe_error', 'Error generating AI edit proposal'),
            4000
          );
          return;
        }
        setPluginJson(payload.pluginJson);
        setPluginPy(payload.pluginPy);
        setHasChanges(true);
        setVibePreviewReady(true);
        openSnackbar(
          t('global_setting.plugins_vibe_ready', 'AI proposal ready. Review it and save when you are satisfied.'),
          3500
        );
      }
    );
    return unsubscribe;
  }, [openSnackbar, selectedFolder, subscribeToMessages, t]);

  const handleSave = useCallback(async () => {
    if (!selectedFolder) {
      return;
    }

    try {
      JSON.parse(pluginJson || '{}');
    } catch {
      openSnackbar(t('global_setting.plugins_invalid_json', 'Plugin JSON is invalid'), 3500);
      return;
    }

    setSaving(true);
    try {
      const data = await savePluginFiles(selectedFolder, {
        manifestFile,
        pluginJson,
        pluginPy,
        changeSource: vibePreviewReady ? 'ai' : 'manual',
      });
      if (data.status !== 'success') {
        throw new Error(data.message || t('global_setting.plugins_save_error', 'Error saving plugin'));
      }

      openSnackbar(t('global_setting.plugins_save_success', 'Plugin saved successfully'), 2500);
      const plugin = data.plugin || {};
      setManifestFile(plugin.manifestFile || manifestFile);
      setScriptFile(plugin.scriptFile || scriptFile);
      setPluginJson(data.pluginJson || pluginJson);
      setPluginPy(data.pluginPy || pluginPy);
      setMemoryJson(data.memoryJson || memoryJson);
      setHasChanges(false);
      setVibePreviewReady(false);
      setVibeConfirmOpen(false);
      setVibeAnalysis(null);
      await loadPluginVersions(selectedFolder);
      await loadPlugins();
    } catch (error) {
      openSnackbar(
        error?.message || t('global_setting.plugins_save_error', 'Error saving plugin'),
        3500
      );
    } finally {
      setSaving(false);
    }
  }, [
    selectedFolder,
    manifestFile,
    pluginJson,
    pluginPy,
    scriptFile,
    memoryJson,
    openSnackbar,
    loadPlugins,
    loadPluginVersions,
    vibePreviewReady,
    t
  ]);

  const handleRestorePluginVersion = useCallback(async (version) => {
    const versionId = String(version?.id || '').trim();
    if (!selectedFolder || !versionId || saving || restoringVersionId) {
      return;
    }

    const shouldRestore = window.confirm(
      t(
        'global_setting.plugins_versions_restore_confirm',
        'Restore version {{number}}? The current plugin will first be saved as a new version.',
        { number: version?.versionNumber || '?' }
      )
    );
    if (!shouldRestore) {
      return;
    }

    setRestoringVersionId(versionId);
    try {
      const data = await restorePluginVersion(selectedFolder, versionId);
      if (data.status !== 'success') {
        throw new Error(data.message || t('global_setting.plugins_versions_restore_error', 'Error restoring plugin version'));
      }
      applyPluginFilesPayload(data);
      setHasChanges(false);
      setVibePreviewReady(false);
      setVibeConfirmOpen(false);
      setVibeAnalysis(null);
      await loadPluginVersions(selectedFolder);
      openSnackbar(
        t('global_setting.plugins_versions_restore_success', 'Plugin version restored successfully'),
        3000
      );
    } catch (error) {
      openSnackbar(
        error?.message || t('global_setting.plugins_versions_restore_error', 'Error restoring plugin version'),
        4000
      );
    } finally {
      setRestoringVersionId('');
    }
  }, [
    applyPluginFilesPayload,
    loadPluginVersions,
    openSnackbar,
    restoringVersionId,
    saving,
    selectedFolder,
    t,
  ]);

  const handleDeletePlugin = useCallback(async () => {
    if (!selectedFolder) {
      return;
    }
    setDeleting(true);
    try {
      const data = await deletePlugin(selectedFolder);
      if (data.status !== 'success') {
        throw new Error(data.message || t('global_setting.plugins_delete_error', 'Error deleting plugin'));
      }

      setDeleteDialogOpen(false);
      openSnackbar(t('global_setting.plugins_delete_success', 'Plugin deleted successfully'), 2500);
      setDeletedPluginsRefresh(value => value + 1);
      setEditDialogOpen(false);
      setMemoryDialogOpen(false);
      setSelectedFolder('');
      setPluginName('');
      setPluginType('');
      setPluginKind('');
      setPluginJson('');
      setPluginPy('');
      setMemoryJson('');
      setPluginVersions([]);
      setPluginVersionsError('');
      setHasChanges(false);
      setVibeRequest('');
      setVibePreviewReady(false);
      setVibeConfirmOpen(false);
      setVibeAnalysis(null);
      await loadPlugins();
    } catch (error) {
      openSnackbar(
        error?.message || t('global_setting.plugins_delete_error', 'Error deleting plugin'),
        3500
      );
    } finally {
      setDeleting(false);
    }
  }, [selectedFolder, openSnackbar, loadPlugins, t]);

  const handleExportPlugin = useCallback(async () => {
    if (!selectedFolder) {
      return;
    }
    setExporting(true);
    try {
      const response = await apiBinaryRequest(
        `/api/v1/settings/plugins/${encodeURIComponent(selectedFolder)}/archive`
      );
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || `${selectedFolder}.zip`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      openSnackbar(t('global_setting.plugins_export_success', 'Plugin exported as ZIP'), 2500);
    } catch (error) {
      openSnackbar(
        error?.message || t('global_setting.plugins_export_error', 'Error exporting plugin'),
        3500
      );
    } finally {
      setExporting(false);
    }
  }, [apiBinaryRequest, openSnackbar, selectedFolder, t]);

  const handleImportComplete = useCallback(async (data) => {
    setImporting(true);
    try {
      const nextFolder = String(data?.folderName || data?.folder_name || '').trim();
      await loadPlugins();
      if (nextFolder) {
        await loadPluginFiles(nextFolder);
      }
    } finally {
      setImporting(false);
    }
  }, [loadPluginFiles, loadPlugins]);

  const handleReloadPlugins = useCallback(async () => {
    setReloading(true);
    try {
      const data = await reloadPlugins();
      if (data.status !== 'success') {
        throw new Error(data.message || t('global_setting.plugins_reload_error', 'Error reloading plugins'));
      }
      openSnackbar(
        t('global_setting.plugins_reload_success', 'Plugins reloaded successfully'),
        2500
      );
      await loadPlugins();
    } catch (error) {
      openSnackbar(
        error?.message || t('global_setting.plugins_reload_error', 'Error reloading plugins'),
        3500
      );
    } finally {
      setReloading(false);
    }
  }, [openSnackbar, loadPlugins, t]);

  const handleVibeEdit = useCallback(() => {
    const trimmedRequest = String(vibeRequest || '').trim();
    if (!selectedFolder || !trimmedRequest || typeof sendMessage !== 'function') {
      return;
    }
    setVibeConfirmOpen(false);
    setVibeAnalysis(null);
    setVibeClarificationAnswers({});
    setVibeLoading(true);
    setVibePreviewReady(false);
    sendMessage({
      command: 'vibeEditGeneratedPlugin',
      data: {
        folderName: selectedFolder,
        type: pluginType,
        request: trimmedRequest,
      },
    });
  }, [pluginType, selectedFolder, sendMessage, vibeRequest]);

  const handleConfirmVibeEdit = useCallback((dialogAnswers = null) => {
    const trimmedRequest = String(vibeRequest || '').trim();
    const clarificationAnswers = {
      ...(vibeClarificationAnswers && typeof vibeClarificationAnswers === 'object'
        ? vibeClarificationAnswers
        : {}),
      ...(dialogAnswers && typeof dialogAnswers === 'object' ? dialogAnswers : {}),
    };
    const clarificationQuestions = Array.isArray(vibeAnalysis?.clarifying_questions)
      ? vibeAnalysis.clarifying_questions.filter((question) => String(question || '').trim())
      : [];
    const missingAnswerNumbers = clarificationQuestions
      .map((_, index) => index)
      .filter((index) => !String(clarificationAnswers[index] || '').trim())
      .map((index) => index + 1);
    if (missingAnswerNumbers.length > 0 || (vibeAnalysis?.can_proceed === false && !clarificationQuestions.length)) {
      openSnackbar(
        t(
          'global_setting.plugins_vibe_missing_answers',
          'Answer all clarification questions before continuing. Missing question(s): {{numbers}}.',
          { numbers: missingAnswerNumbers.join(', ') || '?' }
        ),
        4000
      );
      return;
    }
    if (
      !selectedFolder
      || !trimmedRequest
      || !vibeAnalysis
      || typeof sendMessage !== 'function'
    ) {
      return;
    }
    setVibeConfirmOpen(false);
    setVibeLoading(true);
    setVibePreviewReady(false);
    sendMessage({
      command: 'vibeApplyGeneratedPluginEdit',
      data: {
        folderName: selectedFolder,
        type: pluginType,
        request: trimmedRequest,
        analysis: vibeAnalysis,
        clarificationAnswers,
      },
    });
  }, [
    pluginType,
    selectedFolder,
    sendMessage,
    openSnackbar,
    t,
    vibeAnalysis,
    vibeClarificationAnswers,
    vibeRequest,
  ]);

  const handleVibeRequestChange = useCallback((value) => {
    setVibeRequest(value);
    setVibeAnalysis(null);
    setVibeClarificationAnswers({});
    setVibePreviewReady(false);
  }, []);

  const handleVibeClarificationAnswerChange = useCallback((index, value) => {
    setVibeClarificationAnswers((currentAnswers) => ({
      ...currentAnswers,
      [index]: value,
    }));
  }, []);

  const hasMemory = Boolean(memoryJson && String(memoryJson).trim());
  const canVibeEdit = (
    String(pluginKind || '').trim().toLowerCase() === 'action'
    && /^auto_/i.test(String(selectedFolder || '').trim())
  );
  const openPluginEditor = useCallback(
    (folderName) => {
      if (!folderName) {
        return;
      }
      if (hasChanges && selectedFolder && selectedFolder !== folderName) {
        const shouldContinue = window.confirm(
          t('global_setting.plugins_unsaved_confirm', 'You have unsaved changes. Discard them?')
        );
        if (!shouldContinue) {
          return;
        }
      }
      setEditDialogOpen(true);
      setSelectedFolder(folderName);
      setVibeRequest('');
      setVibePreviewReady(false);
      setVibeConfirmOpen(false);
      setVibeAnalysis(null);
      loadPluginFiles(folderName);
      loadPluginLogs(folderName);
    },
    [hasChanges, selectedFolder, loadPluginFiles, loadPluginLogs, t]
  );

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t(
          'global_setting.plugins_description',
          'Read plugin memory and edit plugin.json / python code directly for auto_* plugins only.'
        )}
      </Alert>

      <DeletedPluginsPanel refreshKey={deletedPluginsRefresh} onRestored={loadPlugins} />

      <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
        <Paper variant="outlined" sx={{ width: '100%' }}>
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e5e7eb' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('global_setting.plugins_section', 'Plugins')}
              </Typography>
              <Stack direction="row" spacing={1}>
                <PluginImportButton
                  disabled={importing || loadingList}
                  onImportComplete={handleImportComplete}
                  openSnackbar={openSnackbar}
                />
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={loadPlugins}
                  disabled={loadingList || importing}
                >
                  {t('global_setting.refresh', 'Refresh')}
                </Button>
              </Stack>
            </Stack>
          </Box>
          {loadingList ? (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <List dense disablePadding>
              {plugins.map((plugin) => {
                const kindMeta = getPluginKindMeta(plugin);
                const aiGenerated = isAiGeneratedPlugin(plugin);
                return (
                  <ListItem
                    key={plugin.folderName}
                    secondaryAction={(
                      <Tooltip title={t('global_setting.plugins_open_editor', 'Open editor')}>
                        <IconButton
                          edge="end"
                          onClick={() => openPluginEditor(plugin.folderName)}
                          disabled={vibeLoading}
                        >
                          <MoreHorizIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  >
                    <ListItemText
                      primary={(
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {plugin.name || plugin.folderName}
                          </Typography>
                          {aiGenerated && (
                            <Tooltip title={t('generic_connector.ai_generated_badge', 'AI-generated plugin')} arrow>
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '50%',
                                  color: '#ffffff',
                                  background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                                  boxShadow: '0 4px 10px rgba(37, 99, 235, 0.35)',
                                }}
                              >
                                <AutoAwesomeIcon sx={{ fontSize: 12 }} />
                              </Box>
                            </Tooltip>
                          )}
                          <Chip
                            size="small"
                            label={kindMeta.label}
                            color={kindMeta.color}
                            variant="outlined"
                          />
                        </Stack>
                      )}
                      secondary={`${plugin.folderName} - ${plugin.type || t('global_setting.plugins_kind_unknown', 'Unknown')}`}
                    />
                  </ListItem>
                );
              })}
              {!plugins.length && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('global_setting.plugins_no_plugins', 'No plugins found')}
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </Paper>

      </Stack>

      <PluginEditorDialog
        open={editDialogOpen}
        onClose={() => {
          if (!saving && !deleting && !vibeLoading && !vibeConfirmOpen) {
            setEditDialogOpen(false);
          }
        }}
        t={t}
        selectedFolder={selectedFolder}
        pluginName={pluginName}
        pluginKind={pluginKind}
        pluginType={pluginType}
        manifestFile={manifestFile}
        scriptFile={scriptFile}
        loadingFiles={loadingFiles}
        pluginJson={pluginJson}
        pluginPy={pluginPy}
        onPluginJsonChange={(value) => {
          setPluginJson(value);
          setHasChanges(true);
          setVibePreviewReady(false);
        }}
        onPluginPyChange={(value) => {
          setPluginPy(value);
          setHasChanges(true);
          setVibePreviewReady(false);
        }}
        hasMemory={hasMemory}
        hasChanges={hasChanges}
        deleting={deleting}
        saving={saving}
        pluginLogs={pluginLogs}
        pluginLogsLoading={pluginLogsLoading}
        pluginLogsError={pluginLogsError}
        onRefreshPluginLogs={() => loadPluginLogs(selectedFolder)}
        pluginVersions={pluginVersions}
        pluginVersionsLoading={pluginVersionsLoading}
        pluginVersionsError={pluginVersionsError}
        restoringVersionId={restoringVersionId}
        onRefreshPluginVersions={() => loadPluginVersions(selectedFolder)}
        onRestorePluginVersion={handleRestorePluginVersion}
        exporting={exporting}
        importing={importing}
        reloading={reloading}
        canVibeEdit={canVibeEdit}
        vibeRequest={vibeRequest}
        vibeLoading={vibeLoading}
        vibePreviewReady={vibePreviewReady}
        onVibeRequestChange={handleVibeRequestChange}
        onVibeEdit={handleVibeEdit}
        onOpenMemory={() => setMemoryDialogOpen(true)}
        onOpenDelete={() => setDeleteDialogOpen(true)}
        onExport={handleExportPlugin}
        onReload={handleReloadPlugins}
        onSave={handleSave}
      />

      <PluginVibeConfirmationDialog
        open={vibeConfirmOpen}
        onClose={() => setVibeConfirmOpen(false)}
        t={t}
        pluginName={pluginName || selectedFolder}
        vibeAnalysis={vibeAnalysis}
        vibeRequest={vibeRequest}
        vibeLoading={vibeLoading}
        clarificationAnswers={vibeClarificationAnswers}
        onClarificationAnswerChange={handleVibeClarificationAnswerChange}
        onConfirm={handleConfirmVibeEdit}
      />

      <Dialog
        open={memoryDialogOpen}
        onClose={() => setMemoryDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{t('global_setting.plugins_memory_dialog_title', 'Plugin memory')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('global_setting.plugins_memory_label', 'memory.json (read-only)')}
            value={memoryJson || ''}
            multiline
            minRows={18}
            fullWidth
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: 'monospace',
                fontSize: '0.82rem',
                backgroundColor: '#fafafa'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemoryDialogOpen(false)}>
            {t('global_setting.cancel', 'Cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleting) {
            setDeleteDialogOpen(false);
          }
        }}
      >
        <DialogTitle>{t('global_setting.plugins_delete_confirm_title', 'Delete plugin')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'global_setting.plugins_delete_confirm_message',
              'Are you sure you want to delete plugin "{{name}}"?',
              { name: pluginName || selectedFolder }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setDeleteDialogOpen(false)}>
            {t('global_setting.cancel', 'Cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={handleDeletePlugin}
          >
            {deleting
              ? t('global_setting.plugins_deleting', 'Deleting...')
              : t('global_setting.plugins_delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export { PluginSettings };
